<template>
  <div>
    <div
      v-if="hasZeroOrOneClients || disable"
      class="full-width"
      data-e2e="name-selected-client"
    >
      <div class="row items-center justify-center">
        <template v-if="isLoadingClients || isChangingClient">
          <hr-loading-spinner class="q-mr-md" />
          <span class="ellipsis client-name">
            {{ $t('common.loading') }}
          </span>
        </template>
        <template v-else>
          <hr-avatar
            v-if="selectedClient"
            :src="getClientProfileImagePath(selectedClient)"
            size="sm"
            class="q-mr-sm"
          /><span class="ellipsis client-name">{{ currentClientName }}</span>
        </template>
      </div>
    </div>
    <hr-dropdown-menu
      v-else
      ref="clientDropdown"
      v-model="isShowing"
      variant="text"
      :legacy-props="{
        flat: !outline,
        outline: outline,
        'no-caps': true,
        'dropdown-icon': 'keyboard_arrow_down',

      }"
      :loading="isLoadingClients || isChangingClient"
      class="full-width"
      content-class="overflow-hidden-y"
      data-e2e="dropdown-selector-client"
      @show="onShowClients"
      @before-show="onRefreshVirtualScroll"
    >
      <template
        v-if="selectedClient && !noLabel"
        #avatar
      >
        <hr-avatar
          :src="getClientProfileImagePath(selectedClient)"
          size="sm"
          class="q-mr-sm"
        />
      </template>
      <template
        v-if="!noLabel"
        #label
      >
        <span
          class="ellipsis client-name"
          data-e2e="name-selected-client"
        >
          {{ currentClientName }}
        </span>
      </template>
      <hr-list>
        <hr-text-field
          v-if="numberOfClients >= 5"
          v-model="clientNameFilter"
          variant="outlined"
          :label="$t('clientSelector.inputFindClientLabel')"
          debounce="300"
          clearable
          dense
          autofocus
          full-width
          hide-bottom-space
          data-e2e="input-search-selector-client"
          @keyup.enter.prevent="onChangeToFirstClient"
          @update:model-value="onRefreshVirtualScroll"
        />
        <hr-virtual-scroll
          ref="personList"
          v-slot="{ item: client, }"
          :items="filteredPersons"
          :virtual-scroll-item-size="48"
          :virtual-scroll-slice-ratio-after="2"
          style="max-height: 300px;"
          data-e2e="list-client-selector"
        >
          <hr-item-label
            v-if="client.isHeader"
            :key="client.type"
            header
          >
            {{ client.type === 'client'
              ? $t('clientSelector.headlineClients')
              : $t('clientSelector.headlineStaff') }}
          </hr-item-label>
          <hr-list-item
            v-else
            :key="client.id"
            v-close-popup
            :active="client.id === selectedClientId"
            :icon="client.type === 'staff' ? 'supervised_user_circle' : null"
            :label="client.alias || client.clientName"
            clickable
            :class="{'hover-detect': !$q.platform.is.mobile}"
            data-e2e="list-item-client-selector"
            @click="onChangeClient(client)"
          >
            <template
              v-if="numberOfClients >= 5 && client.type === 'client'"
              #trailing
            >
              <hr-icon-toggle-btn
                :selected="isFavorite(client.id)"
                size="sm"
                icon="favorite_outline"
                selected-icon="favorite"
                :legacy-props="{color: 'primary', dense:true, size: 'md'}"
                :class="{ 'show-on-hover': !isFavorite(client.id) && !$q.platform.is.mobile }"
                @click.stop="onToggleFavorite(client)"
              />
            </template>
          </hr-list-item>
        </hr-virtual-scroll>
      </hr-list>
    </hr-dropdown-menu>
  </div>
</template>
<script>
import {
  chain, find, get, includes, isEmpty, sortBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useClients } from 'src/composables/clients';
import {
  HrAvatar, HrDropdownMenu, HrIconToggleBtn, HrItemLabel, HrList, HrListItem, HrLoadingSpinner,
  HrTextField, HrVirtualScroll,
} from 'src/design-system';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { nextTick } from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

export default {
  name: 'ClientSelector',

  components: {
    HrAvatar,
    HrDropdownMenu,
    HrIconToggleBtn,
    HrItemLabel,
    HrList,
    HrListItem,
    HrLoadingSpinner,
    HrTextField,
    HrVirtualScroll,
  },

  mixins: [Interactions],

  props: {
    returnValue: {
      type: Boolean,
      default: false,
    },
    ignoreCurrentClient: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: Object,
      default: null,
    },
    hideClients: {
      type: Boolean,
      default: false,
    },
    hideStaffMembers: {
      type: Boolean,
      default: false,
    },
    outline: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input'],

  setup() {
    const { getClientProfileImagePath } = useClients();
    return { getClientProfileImagePath };
  },

  data() {
    return {
      isShowing: false,
      isChangingClient: false,

      selectedClient: null,
      clientNameFilter: '',
    };
  },

  computed: {
    ...mapState(auth, ['currentClient', 'userSettings', 'deactivatedClientAllowedToView',
      'hasSupervisorCredentials']),
    ...mapState(clients, ['isLoadingClients', 'clients']),
    ...mapState(network, ['offlineMode']),

    numberOfClients() {
      return this.availablePersons.filter((person) => (!person.isHeader)).length;
    }, // numberOfClients()

    hasZeroOrOneClients() {
      if ((this.numberOfClients === 1) && (isEmpty(this.currentClient))) {
        // Allow user to select the one client s/he has access to
        return false;
      }

      return (this.numberOfClients <= 1) && !this.ignoreCurrentClient;
    }, // hasZeroOrOneClients()

    selectedClientId() {
      if (this.numberOfClients === 0) {
        return null;
      }

      return this.selectedClient?.id || null;
    }, // selectedClientId()

    currentClientName() {
      if (this.numberOfClients === 0) {
        return '';
      }

      return get(this.selectedClient, 'clientName') || this.$t('clientSelector.textSelectClient');
    }, // currentClientName()

    availablePersons() {
      let sortedClients = this.clients.filter((client) => client.status === 'active');

      if (this.offlineMode) {
        sortedClients = sortedClients.filter((client) => this.userSettings?.offlineClients?.includes(client.id));
      }

      if (this.hideClients || this.hideStaffMembers) {
        sortedClients = sortedClients.filter((client) => (
          (!this.hideStaffMembers || client.type !== 'staff')
          && (!this.hideClients || client.type !== 'client')
        ));
      }

      sortedClients = sortBy(sortedClients, (client) => [
        client.type === 'client' ? '0' : '1',
        this.isFavorite(client.id) ? '0' : '1',
        client.alias || client.clientName,
      ]);

      const clientIndex = this.hideClients ? -1 : sortedClients.findIndex((item) => item.type === 'client');
      const staffIndex = this.hideStaffMembers ? -1 : sortedClients.findIndex((item) => item.type === 'staff');

      if (clientIndex !== -1) {
        sortedClients.splice(clientIndex, 0, { isHeader: true, type: 'client' });
      }
      if (staffIndex !== -1) {
        sortedClients.splice(staffIndex + (clientIndex !== -1 ? 1 : 0), 0, { isHeader: true, type: 'staff' });
      }

      return sortedClients;
    },

    filteredPersons() {
      const lowerClientNameFilter = this.clientNameFilter?.trim()?.toLowerCase();

      if (this.clientNameFilter) {
        const matches = this.availablePersons
          .filter((client) => (
            client.isHeader
            || client.alias?.toLowerCase().includes(lowerClientNameFilter)
            || client.clientName?.toLowerCase().includes(lowerClientNameFilter)
          ));

        // Check if there are any clients or staff in the matches
        const hasClients = !this.hideClients && matches.some((client) => client.type === 'client' && !client.isHeader);
        const hasStaff = !this.hideStaffMembers
          && matches.some((client) => client.type === 'staff' && !client.isHeader);

        if ((hasClients && hasStaff)
          || (hasClients && this.hideStaffMembers)
          || (hasStaff && this.hideClients)) {
          return matches;
        }
        return matches.filter((client) => (
          !client.isHeader
          || (client.type === 'client' && hasClients)
          || (client.type === 'staff' && hasStaff)
        ));
      }
      return this.availablePersons;
    },

    favoriteClients() {
      return get(this.userSettings, 'favoriteClients', []);
    },
  },

  watch: {
    currentClient: {
      immediate: true,
      deep: false,
      async handler(client) {
        if (this.ignoreCurrentClient) {
          return;
        }
        if (!isEmpty(client)) {
          this.selectedClient = client;
        } else {
          this.selectedClient = null;
        }
      },
    }, // currentClient()

    defaultValue: {
      immediate: true,
      handler(defaultClient) {
        if (defaultClient === null) {
          return;
        }

        const client = find(this.clientsByAlias, { id: defaultClient?.id });
        if (client) {
          this.selectedClient = client;
        }
      },
    }, // defaultValue()

  },

  async mounted() {
    await this.initializeClients();
  },

  methods: {
    ...mapActions(auth, [
      'changeCurrentClient',
      'changeDeactivatedClientAllowedToView',
      'updateUserSettings',
    ]),
    ...mapActions(clients, ['initializeClients', 'syncClients']),
    ...mapActions(programBehaviourData, ['invalidateBehaviourDataCache']),

    show() {
      if (this.$refs.clientDropdown && !this.isShowing) {
        this.$refs.clientDropdown.show();
      }
    }, // show()

    async onRefreshVirtualScroll() {
      await nextTick();
      this.$refs.personList?.refresh(0);
      this.$refs.personList?.reset();
    }, // refreshVirtualScroll()

    async onShowClients() {
      this.syncClients();
    },

    async onChangeToFirstClient() {
      this.onChangeClient(this.filteredPersons.find((person) => !person.isHeader));
      this.$refs.clientDropdown?.hide();
    }, // onChangeToFirstClient()

    onFavorite(client) {
      const favoriteClients = chain(this.favoriteClients)
        .concat(client.id)
        .compact()
        .uniq()
        .value();

      this.updateUserSettings({ favoriteClients });
      this.onRefreshVirtualScroll();

      this.notifySuccess(this.$t('common.notifyFavorite', { item: client.clientName }));
    },

    onUnfavorite(client) {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t(
          'common.notifyUnfavoriteConfirm',
          { item: client.clientName },
        ),

        () => {
          const favoriteClients = chain(this.favoriteClients)
            .without(client.id)
            .compact()
            .uniq()
            .value();

          this.updateUserSettings({ favoriteClients });
        },
      );
    },

    onToggleFavorite(client) {
      if (this.isFavorite(client.id)) {
        this.onUnfavorite(client);
      } else {
        this.onFavorite(client);
      }
    },

    async onChangeClient(client) {
      if (!client) {
        return;
      }
      if (this.returnValue) {
        this.$emit('input', client);
        this.selectedClient = client;
        return;
      }

      this.isChangingClient = true;
      try {
        await this.changeClient(client);
      } finally {
        this.isChangingClient = false;
      }
    },

    async changeClient(client) {
      const failure = await this.$router.push('/').catch((err) => err);

      if (failure && !isNavigationFailure(failure, NavigationFailureType.duplicated)) {
        return;
      }

      this.$q.loading.show({ delay: 0 });
      try {
        this.invalidateBehaviourDataCache();
        await this.changeCurrentClient(client);

        if (this.hasSupervisorCredentials && this.deactivatedClientAllowedToView) {
          this.changeDeactivatedClientAllowedToView(null);
        }
      } finally {
        this.$q.loading.hide();
      }
    }, // changeClient()

    isFavorite(clientId) {
      return includes(this.favoriteClients, clientId);
    },

  },

};
</script>

<style scoped>
.client-name {
  max-width: 125px;
}

.hover-detect .show-on-hover {
  visibility: hidden;
}

.hover-detect:hover .show-on-hover {
  visibility: visible;
}

</style>
